import React from "react";
import IconCircleInfo from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconCircleInfo.js";
import { IconVoucher } from "@/Icons/IconVoucher";
import IconBed from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconBed.js";
import IconAuto from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconAuto.js";
import IconBusline from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconBusline.js";
import IconRestaurant from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconRestaurant.js";
import IconTrain from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconTrain.js";
import IconPlaneNe from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconPlaneNe.js";
import IconTourOperator from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconTourOperator.js";
import { getRoomsLabels } from "@/lib/Helpers/getRoomsLabels";
import DialogTooltip from "@/components/UI/Tooltip/DialogTooltip";
import Link from "next/link";
import {
  ServicesList,
  RoomLabelsContainer,
  RailAndFlyContainer,
  DialogTooltipContent,
  FlexibleOfferContainer,
} from "./IncludedServicesStyles";
import { OfferRoomDetail } from "@/lib/Types/OfferRoomDetail";
import { MealType } from "@/lib/Types/MealType";
import { Text } from "@lmn/design-system";
import IconShieldCheck from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconShieldCheck.js";
import IconAccessibility from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconAccessibility.js";
import IconLuggage from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconLuggage.js";
import { IconNoTransfer } from "@/Icons/IconNoTransfer";
import { IconWaterfrontRoom } from "@/Icons/IconWaterfrontRoom";
import IconMobile from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconMobile.js";
import IconGallery from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconGallery.js";
import { WhitelabelConfig } from "@/whitelabel/helpers/types";
import { WEG_PERKS, WEG_PERKS_TERMS_LINK, type WegPerkKey } from "@/lib/Helpers/wegPerks";

const wegPerkIcons: Record<WegPerkKey, React.ReactNode> = {
  esim: <IconMobile size={20} aria-hidden="true" />,
  fotos: <IconGallery size={20} aria-hidden="true" />,
};

const oceanViewMap: Record<string, string> = {
  OCEANVIEW: "Meerblick",
  PARTIALOCEANVIEW: "seitlicher Meerblick",
};

// Flexible offer data interface
interface FlexibleOfferData {
  text: string;
  label: string;
}

// Service item interface
interface ServiceItem {
  key: string;
  icon: React.ReactNode;
  content: React.ReactNode;
  shouldShow: boolean;
}

interface IncludedServicesProps {
  flights?: any;
  directFlight?: boolean;
  offerRoomDetails: OfferRoomDetail[] | null;
  mealType: MealType | null;
  transfer: boolean;
  rentalCar?: boolean;
  railAndFly: boolean;
  title?: string;
  tourOperator?: string;
  isNotAvailable?: boolean;
  flexibleOfferData?: FlexibleOfferData | null;
  freeRebookingOfferData?: FlexibleOfferData | null;
  bookOnOptionLabel?: string | null;
  bookOnOptionTooltip?: string | null;
  oceanView?: string;
  minBaggageAllowance?: { minBags: number | null; minWeight: number | null } | null;
  offerForLimitedMobility?: boolean;
  insuranceMessage?: string;
  showNoTransport?: boolean;
  compactTooltips?: boolean;
  showWegPerks?: boolean;
  WlConfig: WhitelabelConfig;
}

const IncludedServices: React.FC<IncludedServicesProps> = ({
  flights,
  directFlight,
  offerRoomDetails,
  mealType,
  transfer,
  rentalCar,
  railAndFly,
  title,
  tourOperator,
  isNotAvailable,
  flexibleOfferData,
  freeRebookingOfferData,
  bookOnOptionLabel,
  bookOnOptionTooltip,
  oceanView,
  minBaggageAllowance,
  offerForLimitedMobility,
  insuranceMessage,
  showNoTransport = false,
  compactTooltips = false,
  showWegPerks = false,
  WlConfig,
}) => {

  // Define service items with their display logic
  const serviceItems: ServiceItem[] = [
    {
      key: "flight",
      icon: <IconPlaneNe size={20} />,
      content: <> {directFlight ? "Direktflug" : "Flug"}</>,
      shouldShow: !!flights,
    },
    {
      key: "rooms",
      icon: <IconBed size={20} />,
      content: (
        <RoomLabelsContainer>
          {getRoomsLabels(offerRoomDetails).map((label, index) => (
            <span key={`${label.text}-${index}`}>{label.text}</span>
          ))}
        </RoomLabelsContainer>
      ),
      shouldShow: !!offerRoomDetails,
    },
    {
      key: "mealType",
      icon: <IconRestaurant size={20} />,
      content: <>{mealType?.name}</>,
      shouldShow: !!mealType,
    },
    {
      key: "transfer",
      icon: <IconBusline size={20} />,
      content: <>inkl. Transfer zum Hotel</>,
      shouldShow: transfer,
    },
    {
      key: "rentalCar",
      icon: <IconAuto size={20} />,
      content: <>inkl. Mietwagen</>,
      shouldShow: !!rentalCar,
    },
    {
      key: "noTransport",
      icon: <IconNoTransfer size={20} />,
      content: <>kein Transfer vor Ort</>,
      shouldShow: showNoTransport && !transfer && !rentalCar,
    },
    {
      key: "railAndFly",
      icon: <IconTrain size={20} />,
      content: (
        <RailAndFlyContainer $compact={compactTooltips}>
          Zug zum Flug innerhalb Deutschlands{" "}
          <DialogTooltip
            trigger={<IconCircleInfo size={16} data-tooltip />}
            title="Zug zum Flug innerhalb Deutschlands"
            content={
              "von/zu jedem deutschen Flughafen, sowie Basel und Salzburg"
            }
          />
        </RailAndFlyContainer>
      ),
      shouldShow: railAndFly,
    },
    {
      key: "oceanView",
      icon: <IconWaterfrontRoom size={20} />,
      content: <>{oceanView && oceanViewMap[oceanView]}</>,
      shouldShow: !!oceanView && !!oceanViewMap[oceanView],
    },
    {
      key: "minBaggageAllowance",
      icon: <IconLuggage size={20} />,
      content: (
        <>
          {minBaggageAllowance?.minBags ? `${minBaggageAllowance.minBags}x ` : ""}
          {minBaggageAllowance?.minWeight
            ? `${minBaggageAllowance.minWeight}kg `
            : ""}
          Gepäck p.P. inkl.
        </>
      ),
      shouldShow: !!minBaggageAllowance,
    },
    {
      key: "insuranceMessage",
      icon: <IconShieldCheck size={20} />,
      content: <>{insuranceMessage}</>,
      shouldShow: !!insuranceMessage,
    },
    {
      key: "offerForLimitedMobility",
      icon: <IconAccessibility size={20} />,
      content: <>geeignet für Personen mit eingeschränkter Mobilität</>,
      shouldShow: !!offerForLimitedMobility,
    },
    {
      key: "tourOperator",
      icon: <IconTourOperator size={20} />,
      content: <>{tourOperator}</>,
      shouldShow: !!tourOperator,
    },
  ];

  // Filter only services that should be shown
  const visibleServices = serviceItems.filter((item) => item.shouldShow);

  return (
    <>
      {title && !isNotAvailable && (
        <Text forceTag={"h4"} weight="bold">
          {title}
        </Text>
      )}
      {isNotAvailable && (
        <Text forceTag={"span"} weight="bold">
          Auf unserer Seite leider ausgebucht!
        </Text>
      )}

      <ServicesList $compact={compactTooltips}>
        {visibleServices.map(({ key, icon, content }) => (
          <Text variant="BodySmall" forceTag="li" key={key}>
            {icon}
            {content}
          </Text>
        ))}

        {showWegPerks &&
          !isNotAvailable &&
          WEG_PERKS.map((perk) => (
            <Text
              variant="BodySmall"
              forceTag="li"
              key={perk.key}
              data-testid={`weg-perk-${perk.key}`}
            >
              {wegPerkIcons[perk.key]}
              <span>{perk.label}</span>
              <DialogTooltip
                title={perk.label}
                className="weg-perk-dialog-tooltip"
                trigger={<IconCircleInfo size={16} data-tooltip />}
                content={
                  <DialogTooltipContent>
                    <Text variant="BodySmall" forceTag="p">
                      {perk.info}
                    </Text>
                    <Link
                      href={WEG_PERKS_TERMS_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Text variant="BodySmall" forceTag="span">
                        Hier geht es zu den Nutzungsbedingungen.
                      </Text>
                    </Link>
                  </DialogTooltipContent>
                }
              />
            </Text>
          ))}

        {WlConfig.hasWegDeVoucher && !isNotAvailable && (
          <Text variant="BodySmall" forceTag="li" data-testid="weg-de-voucher">
            <IconVoucher size={20} aria-hidden="true" />
            <span style={{ whiteSpace: 'nowrap' }}>75 € und 30 € Gutschein</span>
            <DialogTooltip
              title="75 € und 30 € Gutschein"
              className="weg-de-voucher-dialog-tooltip"
              trigger={<IconCircleInfo size={16} data-tooltip />}
              content={
                <DialogTooltipContent>
                  <Text variant="BodySmall" forceTag="p">
                    Buchen Sie jetzt Ihren Urlaub und sichern Sie sich einen 75
                    € und 30 € Geld-zurück-Gutschein für Ihre nächsten Reisen.
                  </Text>
                  <Link
                    href={WlConfig.voucher.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Text variant="BodySmall" forceTag="span">
                      Hier gehts zu den Gutscheinbedingungen.
                    </Text>
                  </Link>
                </DialogTooltipContent>
              }
            />
          </Text>
        )}

        {flexibleOfferData?.text && !isNotAvailable && (
          <Text variant="BodySmall" forceTag="li">
            <FlexibleOfferContainer $compact={compactTooltips}>
              <IconShieldCheck size={20} />
              {flexibleOfferData.label}
              <DialogTooltip
                trigger={<IconCircleInfo size={16} data-tooltip />}
                title={flexibleOfferData.label}
                content={flexibleOfferData.text}
              />
            </FlexibleOfferContainer>
          </Text>
        )}

        {freeRebookingOfferData?.text && !isNotAvailable && (
          <Text variant="BodySmall" forceTag="li">
            <FlexibleOfferContainer $compact={compactTooltips}>
              <IconShieldCheck size={20} />
              {freeRebookingOfferData.label}
              <DialogTooltip
                trigger={<IconCircleInfo size={16} data-tooltip />}
                title={freeRebookingOfferData.label}
                content={freeRebookingOfferData.text}
              />
            </FlexibleOfferContainer>
          </Text>
        )}

        {bookOnOptionLabel && !isNotAvailable && (
          <Text variant="BodySmall" forceTag="li">
            <FlexibleOfferContainer $compact={compactTooltips}>
              <IconShieldCheck size={20} />
              {bookOnOptionLabel}
              {bookOnOptionTooltip && (
                <DialogTooltip
                  trigger={<IconCircleInfo size={16} data-tooltip />}
                  title={bookOnOptionLabel}
                  content={bookOnOptionTooltip}
                />
              )}
            </FlexibleOfferContainer>
          </Text>
        )}
      </ServicesList>
    </>
  );
};

export default IncludedServices;
